/** Renderer <-> main contract for the local (on-device) terminal. */

export const LOCAL_TERMINAL_GET_ENDPOINT_CHANNEL =
  'local-terminal:get-endpoint';
export const LOCAL_TERMINAL_LIST_SESSIONS_CHANNEL =
  'local-terminal:list-sessions';
/** Renderer -> main: kill a host session by id (tab teardown without a live socket). */
export const LOCAL_TERMINAL_KILL_SESSION_CHANNEL =
  'local-terminal:kill-session';
/** Renderer -> main (send, no reply): which host session the active tab shows. */
export const LOCAL_TERMINAL_SET_ACTIVE_SESSION_CHANNEL =
  'local-terminal:set-active-session';
/**
 * Main -> renderer: an agent-run command started. Runs are headless (no tab); the
 * chat card for the tool call shows its output and can attach a tab on request.
 */
export const LOCAL_TERMINAL_SESSION_OPENED_CHANNEL =
  'local-terminal:session-opened';
/** Main -> renderer: an agent-run command finished; payload is the exec result. */
export const LOCAL_TERMINAL_SESSION_CLOSED_CHANNEL =
  'local-terminal:session-closed';

/** Loopback websocket the renderer's xterm connects to; token is sent as the first frame. */
export interface LocalTerminalEndpoint {
  port: number;
  token: string;
}

/** A live shell in the PTY host, as reported by `GET /sessions`. */
export interface LocalTerminalSessionInfo {
  id: string;
  cwd: string;
  cols: number;
  rows: number;
  /** Last OSC 0/2 title the shell set; empty until it does. */
  title: string;
  createdAt: number;
  /** Number of websockets currently attached. */
  clients: number;
  /** The one-shot command for a session started by `/exec`; null for interactive shells. */
  command: string | null;
}

/** Payload of LOCAL_TERMINAL_SESSION_OPENED_CHANNEL. */
export interface LocalTerminalSessionOpened {
  sessionId: string;
  command: string;
  cwd: string;
  /** Backend tool-call id for /exec sessions; binds the chat card to this run. */
  toolCallId?: string;
}

/** Request for the host's `POST /exec` (see pty-host.ts). */
export interface LocalTerminalExecRequest {
  command: string;
  /** Explicit directory; wins over `cwdFromSession`. */
  cwd?: string;
  /** Run in the live cwd of this host session (main fills in the active tab). */
  cwdFromSession?: string;
  timeoutMs?: number;
  /** Backend tool-call id; the host echoes it on the opened event and result. */
  toolCallId?: string;
}

export interface LocalTerminalExecResult {
  sessionId: string;
  command: string;
  cwd: string;
  /** Shell that ran it: "zsh", "bash", "pwsh" (PowerShell 7) or "powershell" (5.1). */
  shell: string;
  /** null when the command was killed (timeout or signal). */
  exitCode: number | null;
  signal: string | null;
  timedOut: boolean;
  durationMs: number;
  /** Trailing output, ANSI stripped, capped by the host. */
  output: string;
  truncated: boolean;
  /** Echoed from the exec request when present. */
  toolCallId?: string;
}

/** Renderer asks main whether the Terminal integration is opted in. */
export const LOCAL_TERMINAL_GET_INTEGRATION_ENABLED_CHANNEL =
  'local-terminal:get-integration-enabled';
/** Renderer tells main the user flipped the Terminal integration toggle. */
export const LOCAL_TERMINAL_SET_INTEGRATION_ENABLED_CHANNEL =
  'local-terminal:set-integration-enabled';
